import React from "react";

import type { WizardStep } from "../types";

interface StepIllustrationProps {
  step: WizardStep;
}

export function StepIllustration({ step }: StepIllustrationProps) {
  switch (step) {
    case 1:
      return <WorkflowSelectionIllustration />;
    case 2:
      return <ParametersIllustration />;
    case 3:
      return <ConfigurationIllustration />;
    case 4:
      return <ReviewIllustration />;
    default:
      return null;
  }
}

// Step 1: Workflow selection with GitHub/Buildkite icons
function WorkflowSelectionIllustration() {
  return (
    <svg viewBox="0 0 120 140" className="w-36 h-44">
      {/* GitHub-style icon */}
      <g transform="translate(20, 15)">
        <circle cx="20" cy="20" r="18" fill="none" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="2" />
        <circle cx="20" cy="15" r="5" fill="hsl(var(--muted-foreground) / 0.5)" />
        <path d="M12 28 Q20 35, 28 28" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2" fill="none" />
      </g>

      {/* Buildkite-style icon */}
      <g transform="translate(60, 15)">
        <rect x="2" y="2" width="36" height="36" rx="4" fill="none" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="2" />
        <rect x="10" y="10" width="8" height="8" fill="hsl(var(--muted-foreground) / 0.5)" />
        <rect x="22" y="10" width="8" height="8" fill="hsl(var(--muted-foreground) / 0.5)" />
        <rect x="10" y="22" width="8" height="8" fill="hsl(var(--muted-foreground) / 0.5)" />
        <rect x="22" y="22" width="8" height="8" fill="hsl(var(--muted-foreground) / 0.5)" />
      </g>

      {/* Selection indicator lines */}
      <path d="M40 55 L60 75" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="1.5" strokeDasharray="3 2" />
      <path d="M80 55 L60 75" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="1.5" strokeDasharray="3 2" />

      {/* Central selector */}
      <circle cx="60" cy="85" r="12" fill="none" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2" />
      <circle cx="60" cy="85" r="4" fill="hsl(var(--muted-foreground) / 0.5)" />

      {/* Workflow file representation */}
      <rect x="35" y="105" width="50" height="25" rx="3" fill="none" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="1.5" />
      <line x1="42" y1="113" x2="70" y2="113" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="1.5" />
      <line x1="42" y1="120" x2="60" y2="120" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="1.5" />

      {/* Arrow connecting to workflow */}
      <path d="M60 97 L60 105" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="1.5" />
    </svg>
  );
}

// Step 2: Parameters (input fields with types)
function ParametersIllustration() {
  return (
    <svg viewBox="0 0 120 140" className="w-36 h-44">
      {/* Parameter table container */}
      <rect x="15" y="15" width="90" height="110" rx="3" fill="none" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2" />

      {/* Header row */}
      <rect x="15" y="15" width="90" height="18" rx="3" fill="hsl(var(--muted-foreground) / 0.4)" />
      <text x="25" y="27" fill="hsl(var(--muted-foreground) / 0.6)" fontSize="7" fontFamily="monospace">
        NAME
      </text>
      <text x="55" y="27" fill="hsl(var(--muted-foreground) / 0.6)" fontSize="7" fontFamily="monospace">
        TYPE
      </text>
      <text x="85" y="27" fill="hsl(var(--muted-foreground) / 0.6)" fontSize="7" fontFamily="monospace">
        REQ
      </text>

      {/* Parameter row 1 */}
      <line x1="15" y1="45" x2="105" y2="45" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="0.5" />
      <rect x="22" y="38" width="25" height="6" rx="1" fill="hsl(var(--muted-foreground) / 0.45)" />
      <text x="60" y="42" fill="hsl(var(--muted-foreground) / 0.5)" fontSize="6" fontFamily="monospace">
        string
      </text>
      <rect x="87" y="37" width="8" height="8" rx="1" fill="hsl(var(--muted-foreground) / 0.45)" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="0.5" />
      <path d="M89 41 L91 43 L94 39" stroke="hsl(var(--muted-foreground) / 0.6)" strokeWidth="1" fill="none" />

      {/* Parameter row 2 */}
      <line x1="15" y1="60" x2="105" y2="60" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="0.5" />
      <rect x="22" y="53" width="20" height="6" rx="1" fill="hsl(var(--muted-foreground) / 0.45)" />
      <text x="60" y="57" fill="hsl(var(--muted-foreground) / 0.5)" fontSize="6" fontFamily="monospace">
        number
      </text>
      <rect x="87" y="52" width="8" height="8" rx="1" fill="none" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="0.5" />

      {/* Parameter row 3 */}
      <line x1="15" y1="75" x2="105" y2="75" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="0.5" />
      <rect x="22" y="68" width="28" height="6" rx="1" fill="hsl(var(--muted-foreground) / 0.45)" />
      <text x="60" y="72" fill="hsl(var(--muted-foreground) / 0.5)" fontSize="6" fontFamily="monospace">
        boolean
      </text>
      <rect x="87" y="67" width="8" height="8" rx="1" fill="hsl(var(--muted-foreground) / 0.45)" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="0.5" />
      <path d="M89 71 L91 73 L94 69" stroke="hsl(var(--muted-foreground) / 0.6)" strokeWidth="1" fill="none" />

      {/* Add parameter button */}
      <rect x="35" y="95" width="50" height="16" rx="2" fill="none" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="1" strokeDasharray="3 2" />
      <line x1="55" y1="100" x2="65" y2="100" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="1.5" />
      <line x1="60" y1="95" x2="60" y2="105" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="1.5" />
    </svg>
  );
}

// Step 3: Configuration (document with gear)
function ConfigurationIllustration() {
  return (
    <svg viewBox="0 0 120 140" className="w-36 h-44">
      {/* Document */}
      <rect x="25" y="10" width="70" height="85" rx="3" fill="none" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2" />

      {/* Document header */}
      <rect x="25" y="10" width="70" height="16" rx="3" fill="hsl(var(--muted-foreground) / 0.4)" />
      <circle cx="35" cy="18" r="3" fill="hsl(var(--muted-foreground) / 0.45)" />
      <line x1="42" y1="18" x2="75" y2="18" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="2" />

      {/* Content lines - description text */}
      <line x1="32" y1="35" x2="88" y2="35" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="1.5" />
      <line x1="32" y1="43" x2="75" y2="43" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="1.5" />
      <line x1="32" y1="51" x2="82" y2="51" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="1.5" />
      <line x1="32" y1="59" x2="65" y2="59" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="1.5" />

      {/* Approval checkbox area */}
      <rect x="32" y="70" width="12" height="12" rx="2" fill="hsl(var(--muted-foreground) / 0.4)" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="1" />
      <path d="M35 76 L38 79 L43 73" stroke="hsl(var(--muted-foreground) / 0.6)" strokeWidth="1.5" fill="none" />
      <line x1="50" y1="76" x2="88" y2="76" stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth="1.5" />

      {/* Gear icon */}
      <circle cx="60" cy="118" r="14" fill="none" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2" />
      <circle cx="60" cy="118" r="5" fill="none" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2" />
      {/* Gear teeth */}
      <line x1="60" y1="102" x2="60" y2="107" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2.5" />
      <line x1="60" y1="129" x2="60" y2="134" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2.5" />
      <line x1="44" y1="118" x2="49" y2="118" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2.5" />
      <line x1="71" y1="118" x2="76" y2="118" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2.5" />
      {/* Diagonal teeth */}
      <line x1="49" y1="107" x2="52" y2="110" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2" />
      <line x1="68" y1="126" x2="71" y2="129" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2" />
      <line x1="49" y1="129" x2="52" y2="126" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2" />
      <line x1="68" y1="110" x2="71" y2="107" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2" />
    </svg>
  );
}

// Step 4: Review (checklist clipboard)
function ReviewIllustration() {
  return (
    <svg viewBox="0 0 120 140" className="w-36 h-44">
      {/* Clipboard */}
      <rect x="25" y="20" width="70" height="100" rx="3" fill="none" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="2" />

      {/* Clipboard clip */}
      <rect x="45" y="12" width="30" height="16" rx="2" fill="hsl(var(--muted-foreground) / 0.4)" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="1.5" />
      <rect x="52" y="18" width="16" height="6" rx="1" fill="hsl(var(--muted-foreground) / 0.2)" />

      {/* Checklist items */}
      <CheckItem y={40} checked />
      <CheckItem y={60} checked />
      <CheckItem y={80} checked />
      <CheckItem y={100} />
    </svg>
  );
}

function CheckItem({ y, checked }: { y: number; checked?: boolean }) {
  return (
    <g>
      {/* Checkbox */}
      <rect
        x="35"
        y={y}
        width="14"
        height="14"
        rx="2"
        fill={checked ? "hsl(var(--muted-foreground) / 0.45)" : "none"}
        stroke="hsl(var(--muted-foreground) / 0.5)"
        strokeWidth="1.5"
      />
      {checked && (
        <path d={`M38 ${y + 7} L41 ${y + 10} L49 ${y + 4}`} stroke="hsl(var(--muted-foreground) / 0.6)" strokeWidth="2" fill="none" />
      )}
      {/* Line */}
      <line x1="55" y1={y + 7} x2="85" y2={y + 7} stroke="hsl(var(--muted-foreground) / 0.45)" strokeWidth={checked ? "1.5" : "1"} />
    </g>
  );
}
